import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CostType } from 'src/app/shared/models/cost-types.model';
import { EmployeeService } from 'src/app/shared/services/employee.service';
import { Subscription, pipe } from 'rxjs';
import { AlertService } from 'src/app/shared/services/alert.service';
import { Permissions } from 'src/app/shared/models/permission.model';
import { Roles } from 'src/app/shared/models/auth/role.model';
import { tap, first } from 'rxjs/operators';
import { CostService } from 'src/app/shared/services/cost.service';
import { Employee } from 'src/app/shared/models/employee.model';
import { ToastrService } from 'ngx-toastr';
import { UserService } from 'src/app/shared/services/user.service';
import { ConfirmService } from 'src/app/shared/services/confirm.service';
import { Genders } from 'src/app/shared/models/person.model';

@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
})
export class EmployeeComponent implements OnInit, OnDestroy {
  employeeForm: FormGroup;
  shouldFormBeLoaded = false;

  isThisNewEmployeePage = true;
  employeeId: number;
  employee$;

  currentUserId: number;
  twoFactorEnabled = false;

  employeeFetchSubscription: Subscription;
  employeeCreateSubscription: Subscription;
  employeeUpdateSubscription: Subscription;
  employeeAnonymizeSubscription: Subscription;

  genders = [
    { name: 'mężczyzna', id: 'male' },
    { name: 'kobieta', id: 'female' },
  ];

  costTypes: CostType[];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private employeeService: EmployeeService,
    private costService: CostService,
    private userService: UserService,
    private confirmService: ConfirmService
  ) {}

  async ngOnInit() {
    this.currentUserId = this.userService.getCurrentUserId();
    this.employeeId = parseInt(
      this.activatedRoute.snapshot.paramMap.get('id'),
      10
    );
    await this.fetchCostTypes();
    this.createFormGroup();
    if (!isNaN(this.employeeId)) {
      this.isThisNewEmployeePage = false;
      this.employee$ = this.employeeService.fetchEmployee(this.employeeId).pipe(
        tap(employee => {
          this.twoFactorEnabled = !!(employee.user && employee.user.twoFactorEnabled);
          this.patchFormGroup(employee);
        })
      );
    }
  }

  // TODO: Find a way to do this without async/await
  async fetchCostTypes() {
    this.costTypes = <CostType[]>await this.costService
      .fetchCostTypes()
      .pipe(first())
      .toPromise();
  }

  patchFormGroup(employee: Employee) {
    this.employeeForm.patchValue({
      ...employee,
      costTypesPossibleToMeritoricalApprove: this.createCheckboxGroup(
        this.costTypes,
        employee.costTypesPossibleToMeritoricalApprove
      ),
      costTypesPossibleToRegister: this.createCheckboxGroup(
        this.costTypes,
        employee.costTypesPossibleToRegister
      ),
      user: {
        ...employee.user,
        roles: this.createRoleGroup(employee.user.roles),
      },
      permissions: this.createPermissionGroup(employee.permissions),
    });
    // console.log(this.employeeForm.value);
  }

  createFormGroup() {
    this.employeeForm = this.fb.group({
      position: [null, [Validators.required]],
      department: [null, [Validators.required]],
      person: this.fb.group({
        firstName: [null, Validators.required],
        lastName: [null, Validators.required],
        phoneNumber: '+48',
        gender: [null, [Validators.required]],
        addresses: this.fb.array([
          this.fb.group({
            address1: [],
            address2: [],
          }),
        ]),
        bankAccounts: this.fb.array([
          this.fb.group({
            number: [null, Validators.required],
            name: [null, Validators.required],
          }),
        ]),
      }),
      user: this.fb.group({
        email: [null, [Validators.required, Validators.email]],
        isActive: true,
        roles: this.fb.group({
          User: true,
          Admin: false,
          Staff: false,
          Manager: false,
        }),
      }),

      permissions: this.fb.group({
        CostRegistration: true,
        MeritoricalApproval: false,
        FormalApproval: false,
      }),
      costTypesPossibleToMeritoricalApprove: this.fb.group(
        this.createCheckboxGroup(this.costTypes)
      ),
      costTypesPossibleToRegister: this.fb.group(
        this.createCheckboxGroup(this.costTypes)
      ),
    });
  }

  submit() {
    const employee = this.prepareObjectFromForm();
    if (this.isThisNewEmployeePage) {
      this.employeeService.createEmployee(employee).subscribe(response => {
        this.toastrService.success('Poprawnie zaktualizowano pracownika.');
        // this.alertService.setMessage('Poprawnie utworzono pracownika.');
        this.router.navigate(['/employees']);
      });
    } else {
      this.employeeService
        .updateEmployee(employee, this.employeeId)
        .subscribe(response => {
          this.toastrService.success('Poprawnie zaktualizowano pracownika.');
          // this.alertService.setMessage('Poprawnie zaktualizowano pracownika.');
        });
    }
  }

  private prepareObjectFromForm(): Employee {
    const transformedForm = {
      ...this.employeeForm.value,
      costTypesPossibleToMeritoricalApprove: this.getSelectedCostTypes(
        this.employeeForm.value.costTypesPossibleToMeritoricalApprove
      ),
      costTypesPossibleToRegister: this.getSelectedCostTypes(
        this.employeeForm.value.costTypesPossibleToRegister
      ),
      permissions: this.getSelectedPermissions(
        this.employeeForm.value.permissions
      ),
      user: {
        ...this.employeeForm.value.user,
        email: this.employeeForm.get('user').get('email').value,
        roles: this.getSelectedRoles(this.employeeForm.value.user.roles),
      },
    };
    // console.log('transformedForm', transformedForm);
    return Employee.adapt(transformedForm);
  }

  private createCheckboxGroup(from: any[], initial?: any[]) {
    const checkboxGroup = {};
    if (initial === undefined) {
      from.map(el => (checkboxGroup[el.id] = false));
    } else {
      from.map(el => {
        const checked = initial.some(initialEl => el.id === initialEl.id);
        checkboxGroup[el.id] = checked;
      });
    }
    return checkboxGroup;
  }

  private createRoleGroup(employeeRoles) {
    const rolesGroup = {};

    Object.keys(Roles).map(role => {
      let checked = false;
      // It ensures that User role is always checked even if it is not in employee object fetched from the server
      Roles[role] === Roles.User
        ? (checked = true)
        : (checked = employeeRoles.some(
            employeeRole => employeeRole === Roles[role]
          ));

      rolesGroup[role] = checked;
    });

    return rolesGroup;
  }

  private createPermissionGroup(employeePermissions) {
    const permissionsGroup = {};

    Object.keys(Permissions).map(permission => {
      let checked = false;
      // It ensures that CostRegistration permission is always checked even if it is not in employee object fetched from the server
      Permissions[permission] === Permissions.CostRegistration
        ? (checked = true)
        : (checked = employeePermissions.some(
            employeePermission => employeePermission === Permissions[permission]
          ));

      permissionsGroup[permission] = checked;
    });

    return permissionsGroup;
  }

  /**
   * In a form cost types are in form {key: id of a cost type, value: true or
   * false}. It filters out id which are true, and parses it to int, then main
   * array with all of the cost type objects which were checked is returned
   * @param costTypes
   * @returns costType objects which were selected in a form.
   */
  getSelectedCostTypes(costTypes) {
    const selectedCostTypesId = Object.keys(costTypes)
      .filter(costTypeId => costTypes[costTypeId])
      .map(costTypeId => parseInt(costTypeId, 10));

    return this.costTypes.filter(costType =>
      selectedCostTypesId.includes(costType.id)
    );
  }

  getSelectedPermissions(permissions): Permissions[] {
    return <Permissions[]>Object.keys(permissions)
      .filter(permission => permissions[permission])
      .map(permission => Permissions[permission]);
  }

  getSelectedRoles(roles): Roles[] {
    return <Roles[]>Object.keys(roles)
      .filter(role => roles[role])
      .map(role => Roles[role]);
  }

  anonymize() {
    // TODO: Update person information and mail random strings.
    this.confirmService
      .openConfirmDialog(
        'Czy na pewno chcesz zanonimizować użytkownika? Zmiany będą nieodwracalne!'
      )
      .afterClosed()
      .subscribe(confirm => {
        if (confirm) {
          this.employeeService.anonymize(this.employeeId).subscribe(
            _ => {
              this.toastrService.success(
                'Pomyślnie zanonimizowano użytkownika.'
              );
              this.router.navigate(['/employees']);
            },
            _ =>
              this.toastrService.error(
                'Użytkownik nie został zanonimizowany, ponieważ ma jeszcze nierozliczone koszty.'
              )
          );
        }
      });
  }

  resetTwoFactor() {
    this.confirmService
      .openConfirmDialog(
        'Czy na pewno chcesz zresetować 2FA tego pracownika? Uwierzytelnianie dwuskładnikowe zostanie wyłączone, a pracownik otrzyma powiadomienie e-mail.'
      )
      .afterClosed()
      .subscribe(confirm => {
        if (confirm) {
          this.employeeService.resetTwoFactor(this.employeeId).subscribe(
            _ => {
              this.twoFactorEnabled = false;
              this.toastrService.success('Pomyślnie zresetowano 2FA pracownika.');
            },
            _ => this.toastrService.error('Nie udało się zresetować 2FA.')
          );
        }
      });
  }

  ngOnDestroy() {}
}
